/**
 * Shared mobile-nav toggle, scroll-shadow header, and backdrop-dismiss behavior.
 *
 * Usage:
 *   import { initMobileNav } from "./nav";
 *   initMobileNav({ headerId: "header", toggleSelector: ".nav-toggle", linksId: "nav-links", backdropId: "nav-backdrop" });
 */

/**
 * @typedef {Object} NavOptions
 * @property {string}  headerId        — id of the <header> element.
 * @property {string}  toggleSelector  — CSS selector for the hamburger toggle *within* the header.
 * @property {string}  linksId         — id of the nav-links container.
 * @property {string}  [backdropId]    — id of the backdrop overlay (optional).
 */

/**
 * Wire up mobile-nav toggling, scroll-shadow, backdrop-click, and outside-click dismiss.
 * @param {NavOptions} opts
 */
export function initMobileNav(opts) {
  var header = document.getElementById(opts.headerId);
  var navToggle = header ? header.querySelector(opts.toggleSelector) : null;
  var navLinks = document.getElementById(opts.linksId);
  var navBackdrop = opts.backdropId ? document.getElementById(opts.backdropId) : null;

  function closeNav() {
    if (header) {
      header.classList.remove("nav-open");
      if (navToggle) navToggle.setAttribute("aria-expanded", "false");
    }
  }

  function openNav() {
    if (header) {
      header.classList.add("nav-open");
      if (navToggle) navToggle.setAttribute("aria-expanded", "true");
    }
  }

  function isNavOpen() {
    return header && header.classList.contains("nav-open");
  }

  // Scroll-triggered shadow
  if (header) {
    var onScroll = function () {
      header.classList.toggle("scrolled", window.scrollY > 10);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
  }

  // Hamburger toggle
  if (navToggle && header) {
    navToggle.addEventListener("click", function () {
      isNavOpen() ? closeNav() : openNav();
    });
  }

  // Close when a link is clicked
  if (navLinks) {
    navLinks.addEventListener("click", function (e) {
      if (e.target.closest("a") || e.target.closest("button")) {
        closeNav();
      }
    });
  }

  // Backdrop click
  if (navBackdrop) {
    navBackdrop.addEventListener("click", closeNav);
  }

  // Outside click
  document.addEventListener("click", function (e) {
    if (!isNavOpen()) return;
    if (header && header.contains(e.target)) return;
    closeNav();
  });
}
